const COGNITO_ERROR_KEYS = {
  NotAuthorizedException: 'auth:errors.cognito_invalid_credentials',
  UserNotFoundException: 'auth:errors.cognito_invalid_credentials',
  UserNotConfirmedException: 'auth:errors.cognito_user_not_confirmed',
  CodeMismatchException: 'auth:errors.cognito_code_mismatch',
  ExpiredCodeException: 'auth:errors.cognito_code_expired',
  LimitExceededException: 'auth:errors.cognito_limit_exceeded',
  TooManyRequestsException: 'auth:errors.cognito_limit_exceeded',
  TooManyFailedAttemptsException: 'auth:errors.cognito_limit_exceeded',
  InvalidPasswordException: 'auth:errors.cognito_password_policy',
  PasswordResetRequiredException: 'auth:errors.cognito_password_reset_required',
  InvalidParameterException: 'auth:errors.cognito_invalid_parameter',
} as const;

const KNOWN_NAMES = new Set(Object.keys(COGNITO_ERROR_KEYS));

/** Namespaced (`auth:`-prefixed) i18n key for a Cognito error. */
export type CognitoErrorKey =
  | (typeof COGNITO_ERROR_KEYS)[keyof typeof COGNITO_ERROR_KEYS]
  | 'auth:errors.generic';

type StripAuthNs<Key> = Key extends `auth:${infer Rest}` ? Rest : never;

/** The same keys relative to the `auth` namespace — for a `t` already bound to it. */
export type CognitoErrorAuthKey = StripAuthNs<CognitoErrorKey>;

export function cognitoErrorKey(error: string | null | undefined): CognitoErrorKey | null {
  if (!error) return null;
  for (const name of KNOWN_NAMES) {
    if (error.includes(name)) {
      return COGNITO_ERROR_KEYS[name as keyof typeof COGNITO_ERROR_KEYS];
    }
  }
  return 'auth:errors.generic';
}

/** `cognitoErrorKey` with the `auth:` prefix stripped, typed for an auth-bound `t`. */
export function cognitoErrorAuthKey(error: string | null | undefined): CognitoErrorAuthKey | null {
  const key = cognitoErrorKey(error);
  // Every key above is statically `auth:`-prefixed, so stripping the prefix
  // provably yields a key of the auth namespace.
  return key === null ? null : (key.replace('auth:', '') as CognitoErrorAuthKey);
}
